"use client";

import { useMemo, useState } from "react";

import { fleetDashboardCardClasses } from "@/components/devices/fleet-dashboard-modern";
import { Card } from "@/components/ui-system";
import type { AlertDto } from "@/lib/api/types";
import { cn } from "@/lib/utils/cn";

const CHART_DAYS = 14;

function startOfLocalDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function localDayKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

type DayBin = { key: string; label: string; count: number };

function buildDayBins(alerts: AlertDto[]): { bins: DayBin[]; totalInWindow: number; maxCount: number } {
  const todayStart = startOfLocalDay(new Date());
  const bins: DayBin[] = [];

  for (let i = CHART_DAYS - 1; i >= 0; i--) {
    const d = new Date(todayStart);
    d.setDate(d.getDate() - i);
    bins.push({
      key: localDayKey(d),
      label: d.toLocaleDateString(undefined, { month: "short", day: "numeric" }),
      count: 0,
    });
  }

  const binByKey = new Map(bins.map((b) => [b.key, b]));
  let totalInWindow = 0;

  for (const a of alerts) {
    const t = new Date(a.created_at).getTime();
    if (Number.isNaN(t)) {
      continue;
    }
    const bin = binByKey.get(localDayKey(new Date(t)));
    if (bin) {
      bin.count += 1;
      totalInWindow += 1;
    }
  }

  const maxCount = Math.max(1, ...bins.map((b) => b.count));
  return { bins, totalInWindow, maxCount };
}

type FleetThresholdPanelProps = {
  alerts: AlertDto[];
};

export function FleetThresholdPanel({ alerts }: FleetThresholdPanelProps) {
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);

  const { bins, totalInWindow, maxCount } = useMemo(() => buildDayBins(alerts), [alerts]);

  const vbW = 400;
  const vbH = 132;
  const padL = 8;
  const padR = 8;
  const padB = 22;
  const padT = 12;
  const chartW = vbW - padL - padR;
  const chartH = vbH - padT - padB;
  const n = bins.length;
  const gap = 3;
  const barW = n > 0 ? Math.max(4, (chartW - gap * (n - 1)) / n) : 0;

  return (
    <Card
      className={cn(
        fleetDashboardCardClasses,
        "flex flex-col overflow-hidden border-0 p-0 shadow-none ring-1 ring-border/70",
      )}
    >
      <div className="border-b border-border/60 px-ds-5 py-ds-4">
        <h2 className="font-sans text-sm font-semibold text-foreground">Recent threshold crossings</h2>
        <p className="mt-0.5 text-caption text-muted">
          Count of alerts created per day (last {CHART_DAYS} days, local timezone). Source: alerts API.
        </p>
      </div>
      <div className="px-ds-4 py-ds-4">
        {totalInWindow === 0 ? (
          <p className="py-ds-8 text-center text-caption text-muted">
            No threshold crossings in the last {CHART_DAYS} days for this workspace.
          </p>
        ) : (
          <>
            <p className="mb-ds-3 text-caption text-muted">
              <span className="font-semibold tabular-nums text-foreground">{totalInWindow}</span> crossing
              {totalInWindow === 1 ? "" : "s"} in window
            </p>
            <svg
              viewBox={`0 0 ${vbW} ${vbH}`}
              className="h-40 w-full overflow-visible"
              role="img"
              aria-label={`Bar chart of threshold crossings over ${CHART_DAYS} days`}
              onMouseLeave={() => setHoverIdx(null)}
            >
              {bins.map((b, i) => {
                const h = (b.count / maxCount) * chartH;
                const x = padL + i * (barW + gap);
                const y = padT + chartH - h;
                const active = hoverIdx === i;
                return (
                  <g key={b.key}>
                    <title>{`${b.label}: ${b.count} alert${b.count === 1 ? "" : "s"}`}</title>
                    <rect
                      x={x}
                      y={y}
                      width={barW}
                      height={Math.max(h, b.count > 0 ? 2 : 0)}
                      rx={3}
                      className={cn(
                        "transition-[fill,opacity]",
                        active ? "fill-primary" : "fill-primary/60",
                      )}
                      onMouseEnter={() => setHoverIdx(i)}
                    />
                    {i % 2 === 0 || i === n - 1 ? (
                      <text
                        x={x + barW / 2}
                        y={vbH - 6}
                        textAnchor="middle"
                        className="fill-muted"
                        style={{ fontSize: 9 }}
                      >
                        {b.label}
                      </text>
                    ) : null}
                  </g>
                );
              })}
            </svg>
          </>
        )}
      </div>
    </Card>
  );
}
